/**
 * Sound Effect Service
 * Quản lý âm thanh feedback cho câu hỏi tương tác
 */

class SoundEffectService {
  private audioContext: AudioContext | null = null
  private enabled = true
  private unlockHandler?: (event: Event) => void
  private readonly unlockEvents = ['pointerdown', 'mousedown', 'touchstart', 'keydown'] as const

  constructor() {
    this.enabled = this.isEnabled()
    this.setupAutoUnlock()
  }

  /**
   * Bật/tắt âm thanh
   */
  setEnabled(enabled: boolean) {
    this.enabled = enabled
    if (typeof window === 'undefined') return

    try {
      if (enabled) {
        localStorage.setItem('soundEnabled', 'true')
      } else {
        localStorage.setItem('soundEnabled', 'false')
      }
    } catch (error) {
      console.warn('Unable to persist sound preference:', error)
    }
  }

  isEnabled(): boolean {
    if (typeof window === 'undefined') return this.enabled

    try {
      const stored = localStorage.getItem('soundEnabled')
      return stored !== 'false' // Mặc định bật
    } catch {
      return this.enabled
    }
  }

  private getOrCreateContext(): AudioContext | null {
    if (typeof window === 'undefined') return null

    if (!this.audioContext) {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

      if (!AudioContextClass) {
        console.warn('Web Audio API không được hỗ trợ trên thiết bị này.')
        return null
      }

      this.audioContext = new AudioContextClass()
      // Khi tạo context, đảm bảo thiết lập unlock handlers
      this.setupAutoUnlock()
    }

    return this.audioContext
  }

  private setupAutoUnlock() {
    if (typeof window === 'undefined' || this.unlockHandler) return

    this.unlockHandler = () => {
      const ctx = this.getOrCreateContext()
      if (!ctx) return

      const playSilentTone = () => {
        try {
          const buffer = ctx.createBuffer(1, 1, ctx.sampleRate)
          const source = ctx.createBufferSource()
          source.buffer = buffer
          source.connect(ctx.destination)
          source.start(0)
        } catch (error) {
          console.warn('Không thể phát âm thanh im lặng để unlock:', error)
        }
      }

      const finalize = () => {
        playSilentTone()
        if (ctx.state === 'running') {
          this.removeUnlockHandlers()
        }
      }

      if (ctx.state === 'suspended') {
        ctx
          .resume()
          .then(finalize)
          .catch(error => {
            console.warn('Không thể resume AudioContext:', error)
          })
      } else {
        finalize()
      }
    }

    this.unlockEvents.forEach(event =>
      window.addEventListener(event, this.unlockHandler as EventListener, { passive: true })
    )
  }

  private removeUnlockHandlers() {
    if (typeof window === 'undefined' || !this.unlockHandler) return

    this.unlockEvents.forEach(event =>
      window.removeEventListener(event, this.unlockHandler as EventListener)
    )
    this.unlockHandler = undefined
  }

  private runWithContext(action: (ctx: AudioContext) => void) {
    if (!this.enabled || !this.isEnabled()) return

    const ctx = this.getOrCreateContext()
    if (!ctx) return

    const execute = () => {
      try {
        action(ctx)
      } catch (error) {
        console.warn('Sound effect error:', error)
      }
    }

    if (ctx.state === 'running') {
      execute()
    } else {
      ctx
        .resume()
        .then(execute)
        .catch(error => {
          console.warn('Không thể kích hoạt AudioContext:', error)
        })
    }
  }

  /**
   * Phát âm thanh khi trả lời ĐÚNG - Tiếng chuông "ding" ngắn
   */
  playCorrect() {
    this.runWithContext(ctx => {
      const now = ctx.currentTime
      const chimeFrequencies = [1318.5, 1567.98, 1760, 1975.53] // 4 tiếng chuông liên tiếp
      const interval = 0.08

      chimeFrequencies.forEach((freq, index) => {
        const osc = ctx.createOscillator()
        const gainNode = ctx.createGain()
        const startTime = now + index * interval

        osc.frequency.setValueAtTime(freq, startTime)

        gainNode.gain.setValueAtTime(0.35, startTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.18)

        osc.connect(gainNode)
        gainNode.connect(ctx.destination)

        osc.start(startTime)
        osc.stop(startTime + 0.2)
      })
    })
  }

  /**
   * Phát âm thanh khi trả lời SAI - Click nhẹ, thấp hơn
   */
  playIncorrect() {
    this.runWithContext(ctx => {
      const now = ctx.currentTime

      const tones = [
        { freqStart: 440, freqEnd: 310, duration: 0.18, gain: 0.22 },
        { freqStart: 280, freqEnd: 180, duration: 0.25, gain: 0.2 }
      ]

      tones.forEach((tone, index) => {
        const osc = ctx.createOscillator()
        const gainNode = ctx.createGain()
        const startTime = now + index * 0.12

        osc.type = 'sawtooth'
        osc.frequency.setValueAtTime(tone.freqStart, startTime)
        osc.frequency.exponentialRampToValueAtTime(tone.freqEnd, startTime + tone.duration)

        gainNode.gain.setValueAtTime(tone.gain, startTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + tone.duration)

        osc.connect(gainNode)
        gainNode.connect(ctx.destination)

        osc.start(startTime)
        osc.stop(startTime + tone.duration)
      })
    })
  }

  /**
   * Phát âm thanh khi hoàn thành section - Âm chúc mừng
   */
  playComplete() {
    this.runWithContext(ctx => {
      const now = ctx.currentTime

      // Tạo 3 nốt nhạc vui tươi: C-E-G (hợp âm C major)
      const notes = [261.63, 329.63, 392.00] // C4, E4, G4
      
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator()
        const gainNode = ctx.createGain()
        
        osc.frequency.setValueAtTime(freq, now + i * 0.1)
        
        gainNode.gain.setValueAtTime(0.12, now + i * 0.1)
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.25)
        
        osc.connect(gainNode)
        gainNode.connect(ctx.destination)
        
        osc.start(now + i * 0.1)
        osc.stop(now + i * 0.1 + 0.25)
      })
    })
  }

  /**
   * Chuỗi "ding" tăng dần cho n sao (1..3) — dùng khi qua ải.
   */
  playStars(count: number) {
    this.runWithContext(ctx => {
      const now = ctx.currentTime
      const notes = [659.25, 783.99, 987.77] // E5, G5, B5
      const n = Math.max(1, Math.min(count, 3))
      for (let i = 0; i < n; i++) {
        const osc = ctx.createOscillator()
        const gainNode = ctx.createGain()
        const t = now + i * 0.22
        osc.frequency.setValueAtTime(notes[Math.min(i, notes.length - 1)], t)
        gainNode.gain.setValueAtTime(0.32, t)
        gainNode.gain.exponentialRampToValueAtTime(0.01, t + 0.25)
        osc.connect(gainNode)
        gainNode.connect(ctx.destination)
        osc.start(t)
        osc.stop(t + 0.27)
      }
    })
  }

  /**
   * Âm "LÊN CẤP" — arpeggio đi lên (C-E-G-C).
   */
  playLevelUp() {
    this.runWithContext(ctx => {
      const now = ctx.currentTime
      const notes = [523.25, 659.25, 783.99, 1046.5]
      notes.forEach((f, i) => {
        const osc = ctx.createOscillator()
        const g = ctx.createGain()
        const t = now + i * 0.1
        osc.type = 'triangle'
        osc.frequency.setValueAtTime(f, t)
        g.gain.setValueAtTime(0.26, t)
        g.gain.exponentialRampToValueAtTime(0.01, t + 0.3)
        osc.connect(g)
        g.connect(ctx.destination)
        osc.start(t)
        osc.stop(t + 0.32)
      })
    })
  }

  /**
   * Fanfare lớn khi "hạ trùm" (hoàn thành cả phần).
   */
  playFanfare() {
    this.runWithContext(ctx => {
      const now = ctx.currentTime
      const seq: Array<[number, number]> = [
        [523.25, 0], [523.25, 0.12], [523.25, 0.24], [698.46, 0.4], [659.25, 0.56], [783.99, 0.72],
      ]
      seq.forEach(([f, dt]) => {
        const osc = ctx.createOscillator()
        const g = ctx.createGain()
        const t = now + dt
        osc.type = 'sawtooth'
        osc.frequency.setValueAtTime(f, t)
        g.gain.setValueAtTime(0.18, t)
        g.gain.exponentialRampToValueAtTime(0.01, t + 0.3)
        osc.connect(g)
        g.connect(ctx.destination)
        osc.start(t)
        osc.stop(t + 0.32)
      })
    })
  }

  /**
   * Phát âm thanh khi hỏi AI - Âm bí ẩn, hấp dẫn kiểu "whoosh"
   */
  playAIThinking() {
    this.runWithContext(ctx => {
      const now = ctx.currentTime

      // Tạo âm "whoosh" bí ẩn với sweep từ thấp lên cao
      const osc = ctx.createOscillator()
      const gainNode = ctx.createGain()

      // Sweep từ 400Hz lên 1200Hz (âm thanh bí ẩn, futuristic)
      osc.frequency.setValueAtTime(400, now)
      osc.frequency.exponentialRampToValueAtTime(1200, now + 0.3)

      // Volume tăng mạnh để nghe rõ trên mobile
      gainNode.gain.setValueAtTime(0.05, now)
      gainNode.gain.linearRampToValueAtTime(0.3, now + 0.1)
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.35)

      // Kết nối và phát
      osc.connect(gainNode)
      gainNode.connect(ctx.destination)

      osc.start(now)
      osc.stop(now + 0.35)
    })
  }
}

// Export singleton instance
export const soundService = new SoundEffectService()

